import { useIntl } from "react-intl";
import { DataTableBooleanColumn } from "~components/DataTable/components/DataTableBooleanColumn/DataTableBooleanColumn.tsx";
import { DataTableStringColumn } from "~components/DataTable/components/DataTableStringColumn/DataTableStringColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { useMatchProfileService } from "~pages/MatchProfiles/hooks/useMatchProfileService.ts";
import { MatchProfileTableColumns } from "~pages/MatchProfiles/MatchProfileListPage/enums/matchProfileTableColumns.ts";
import type { UpdateMatchProfileModel } from "~pages/MatchProfiles/types/updateMatchProfileModel.ts";
import type { FixtureProfile } from "~types/fixtureProfile.ts";

export const useGetMatchProfileTableColumns = (massEditMode: boolean): Array<DataTableColumn<FixtureProfile>> => {
  const { formatMessage } = useIntl();
  const { updateMatchProfile } = useMatchProfileService();

  const updateMatchProfileField = async (row: FixtureProfile & DataTableRow, updateData: Omit<UpdateMatchProfileModel, "profileId">) => {
    row.disabled = true;
    try {
      await updateMatchProfile(row.profileId, updateData);
    } finally {
      row.disabled = false;
    }
  };

  return [
    {
      key: MatchProfileTableColumns.Name,
      title: formatMessage({ id: "name" }),
      dataIndex: "name",
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(name) => updateMatchProfileField(row, { name })}
        />
      ),
    },
    {
      key: MatchProfileTableColumns.ProfileId,
      title: formatMessage({ id: "match.profile.id" }),
      dataIndex: "profileId",
      copyable: true,
      width: 320,
    },
    {
      key: MatchProfileTableColumns.DefaultProfile,
      title: formatMessage({ id: "defaultProfile" }),
      dataIndex: "defaultProfile",
      width: 180,
      render: (value: boolean | null) => <DataTableBooleanColumn value={value} />,
    },
    {
      key: MatchProfileTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      copyable: true,
      width: 220,
    },
  ];
};
